import React, { useState, useEffect } from 'react';
import { ClipboardList, RefreshCw, Mail, Phone, Building2, Calendar } from 'lucide-react';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Waitlist() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        headers: { 'Authorization': `Bearer ${api.getToken()}` }
      });
      if (res.status === 403) {
        setError('You do not have access to the waitlist.');
      } else if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setCount(data.count || 0);
      } else {
        setError('Failed to load waitlist.');
      }
    } catch (e) {
      setError('Failed to load waitlist.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="waitlist-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
            <ClipboardList size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Waitlist</h1>
            <p className="text-sm text-zinc-500" data-testid="waitlist-count">{count} signup{count === 1 ? '' : 's'} from streakfree.app</p>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="waitlist-refresh-button"
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="waitlist-error">{error}</div>
      )}

      {!error && !loading && items.length === 0 && (
        <div className="p-10 text-center bg-white border border-zinc-200 rounded-2xl" data-testid="waitlist-empty">
          <ClipboardList size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-600 font-medium">No signups yet</p>
          <p className="text-sm text-zinc-400 mt-1">Waitlist signups from streakfree.app will appear here.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="p-4 bg-white border border-zinc-200 rounded-xl" data-testid={`waitlist-item-${item.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-zinc-900">{item.name || 'No name given'}</p>
                <div className="mt-1 space-y-0.5 text-sm text-zinc-600">
                  <p className="flex items-center gap-2"><Mail size={13} className="text-zinc-400" /> {item.email}</p>
                  {item.mobile && <p className="flex items-center gap-2"><Phone size={13} className="text-zinc-400" /> {item.mobile}</p>}
                  {item.business && <p className="flex items-center gap-2"><Building2 size={13} className="text-zinc-400" /> {item.business}</p>}
                </div>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-zinc-400"><Calendar size={12} /> {formatDate(item.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
